/**
 * Preprocess markdown content to convert mkdocs-specific syntax
 * to formats that react-markdown can render
 */

const ADMONITION_TYPES = {
  note: { icon: 'ℹ️', color: 'blue' },
  warning: { icon: '⚠️', color: 'yellow' },
  danger: { icon: '🚫', color: 'red' },
  tip: { icon: '💡', color: 'green' },
  important: { icon: '❗', color: 'purple' },
  info: { icon: 'ℹ️', color: 'blue' },
  success: { icon: '✅', color: 'green' },
  error: { icon: '❌', color: 'red' },
  example: { icon: '📝', color: 'indigo' },
};

/**
 * Convert mkdocs admonitions to HTML blocks
 * Syntax: !!! warning "Title" (always open)
 *         ??? warning "Title" (collapsible)
 *             Content here
 */
function convertAdmonitions(markdown) {
  const lines = markdown.split('\n');
  const result = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Check for admonition start (both !!! and ???)
    const match = line.match(/^(\?{3}|!{3})\s+(\w+)(?:\s+"([^"]+)")?/);

    if (match) {
      const [, marker, type, title] = match;
      const admonitionType = type.toLowerCase();
      const config = ADMONITION_TYPES[admonitionType] || ADMONITION_TYPES.note;
      const isCollapsible = marker === '???';

      // Collect indented content
      const contentLines = [];
      i++;

      while (i < lines.length && (lines[i].startsWith('    ') || lines[i].trim() === '')) {
        if (lines[i].trim() !== '') {
          contentLines.push(lines[i].substring(4)); // Remove 4-space indent
        } else {
          contentLines.push('');
        }
        i++;
      }

      const content = contentLines.join('\n').trim();
      const displayTitle = title || type.charAt(0).toUpperCase() + type.slice(1);

      if (isCollapsible) {
        // Create collapsible details block
        result.push(`<details class="admonition admonition-${admonitionType} admonition-collapsible">`);
        result.push(`<summary class="admonition-title"><span class="admonition-icon">${config.icon}</span> ${displayTitle}</summary>`);
        result.push(`<div class="admonition-content">\n\n${content}\n\n</div>`);
        result.push(`</details>\n`);
      } else {
        // Create regular admonition block
        result.push(`<div class="admonition admonition-${admonitionType}" data-type="${admonitionType}">`);
        result.push(`<div class="admonition-title"><span class="admonition-icon">${config.icon}</span> ${displayTitle}</div>`);
        result.push(`<div class="admonition-content">\n\n${content}\n\n</div>`);
        result.push(`</div>\n`);
      }

      continue;
    }

    result.push(line);
    i++;
  }

  return result.join('\n');
}

/**
 * Main preprocessor function
 */
export function preprocessMarkdown(markdown) {
  if (!markdown) return '';

  let processed = markdown;

  // Convert admonitions
  processed = convertAdmonitions(processed);

  // You can add more preprocessing here
  // - Convert tabs to spaces
  // - Handle other mkdocs extensions

  return processed;
}
